import React, { useState, useEffect } from 'react';
import { Ensayo, createEnsayo, updateEnsayo, getMedios, getMarcosRegulatorios, getOrganismosControl } from '../services/ensayoService';
import { Button, Checkbox, FormField, Input, Modal, Select } from './ui';

interface EnsayoFormProps {
  ensayo?: Ensayo;
  onEnsayoSaved: () => void;
  onCancel: () => void;
}

const EnsayoForm: React.FC<EnsayoFormProps> = ({ ensayo, onEnsayoSaved, onCancel }) => {
  const [formData, setFormData] = useState<Omit<Ensayo, 'id'>>({
    analito: '',
    medio: 1,
    tipo: '',
    unidad: '',
    estado: 1
  });
  const [medios, setMedios] = useState<{ id: number; medio: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nuevos estados para regulado/controlado
  const [esRegulado, setEsRegulado] = useState(false);
  const [regulado, setRegulado] = useState<{ id_marco: number | ''; valor_min: string; valor_max: string; valor_ref: string; presencia: string }>({ id_marco: '', valor_min: '', valor_max: '', valor_ref: '', presencia: '' });

  const [esControlado, setEsControlado] = useState(false);
  const [controlado, setControlado] = useState<{ id_organismo: number | ''; metodo_deteccion: string; limite_deteccion: string }>({ id_organismo: '', metodo_deteccion: '', limite_deteccion: '' });

  const [marcos, setMarcos] = useState<{ id: number; marco: string }[]>([]);
  const [organismos, setOrganismos] = useState<{ id: number; organismo: string }[]>([]);

  useEffect(() => {
    // Cargar medios disponibles
    const loadInit = async () => {
      try {
        const [mediosData, marcosData] = await Promise.all([
          getMedios(),
          getMarcosRegulatorios()
        ]);
        setMedios(mediosData);
        setMarcos(marcosData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
      }
    };
    loadInit();

    // Si es edición, cargar datos del ensayo
    if (ensayo) {
      setFormData({
        analito: ensayo.analito,
        medio: ensayo.medio,
        tipo: ensayo.tipo,
        unidad: ensayo.unidad,
        estado: ensayo.estado
      });
      if (ensayo.regulado_id) {
        setEsRegulado(true);
        setRegulado({
          id_marco: ensayo.id_marco || '',
          valor_min: ensayo.valor_min?.toString() || '',
          valor_max: ensayo.valor_max?.toString() || '',
          valor_ref: ensayo.valor_ref?.toString() || '',
          presencia: ensayo.presencia || ''
        });
      }
      if (ensayo.controlado_id) {
        setEsControlado(true);
        setControlado({
          id_organismo: ensayo.id_organismo || '',
          metodo_deteccion: ensayo.metodo_deteccion || '',
          limite_deteccion: ensayo.limite_deteccion?.toString() || ''
        });
      }
    }

    // Cargar organismos de control
    const loadOrganismos = async () => {
      try {
        setOrganismos(await getOrganismosControl());
      } catch (e) {
        console.error('Error cargando organismos:', e);
      }
    };
    loadOrganismos();
  }, [ensayo]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'medio' || name === 'estado' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload: any = { ...formData };
      if (esRegulado) {
        payload.regulado = {
          id: ensayo?.regulado_id || undefined,
          id_marco: regulado.id_marco || 0,
          valor_min: regulado.valor_min !== '' ? Number(regulado.valor_min) : 0,
          valor_max: regulado.valor_max !== '' ? Number(regulado.valor_max) : 0,
          valor_ref: regulado.valor_ref !== '' ? Number(regulado.valor_ref) : 0,
          presencia: regulado.presencia || ''
        };
      } else if (ensayo?.regulado_id) {
        payload.regulado = { id: ensayo.regulado_id };
        payload.remove_regulado = true;
      }
      if (esControlado) {
        payload.controlado = {
          id: ensayo?.controlado_id || undefined,
          id_organismo: controlado.id_organismo || 0,
          metodo_deteccion: controlado.metodo_deteccion || '',
          limite_deteccion: controlado.limite_deteccion !== '' ? Number(controlado.limite_deteccion) : 0
        };
      } else if (ensayo?.controlado_id) {
        payload.controlado = { id: ensayo.controlado_id };
        payload.remove_controlado = true;
      }
      if (ensayo) {
        await updateEnsayo(ensayo.id, payload);
      } else {
        await createEnsayo(payload);
      }
      onEnsayoSaved();
    } catch (err: any) {
      console.error('Error al guardar ensayo:', err);
      setError(err.response?.data?.message || 'Error al guardar el ensayo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={ensayo ? 'Editar Ensayo' : 'Crear Nuevo Ensayo'}
      onClose={onCancel}
      size="lg"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" form="ensayo-form" loading={loading}>
            {ensayo ? 'Actualizar' : 'Crear'}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form id="ensayo-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Analito" htmlFor="analito" required>
            <Input
              type="text"
              id="analito"
              name="analito"
              value={formData.analito}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField label="Medio" htmlFor="medio" required>
            <Select
              id="medio"
              name="medio"
              value={formData.medio}
              onChange={handleChange}
              required
            >
              {medios.map((medio) => (
                <option key={medio.id} value={medio.id}>
                  {medio.medio}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Tipo" htmlFor="tipo" required>
            <Input
              type="text"
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField label="Unidad" htmlFor="unidad" required>
            <Input
              type="text"
              id="unidad"
              name="unidad"
              value={formData.unidad}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField label="Estado" htmlFor="estado">
            <Select
              id="estado"
              name="estado"
              value={formData.estado}
              onChange={handleChange}
            >
              <option value={1}>Activo</option>
              <option value={0}>Inactivo</option>
            </Select>
          </FormField>
        </div>

        {/* Sección ensayos regulados */}
        <div className="border-t border-slate-200 pt-4">
          <div className="mb-3">
            <Checkbox
              label="Ensayo regulado"
              id="esRegulado"
              checked={esRegulado}
              onChange={(e) => setEsRegulado(e.target.checked)}
            />
          </div>
          {esRegulado && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Marco regulatorio" htmlFor="id_marco">
                <Select
                  id="id_marco"
                  value={regulado.id_marco}
                  onChange={(e) => setRegulado(prev => ({ ...prev, id_marco: e.target.value ? Number(e.target.value) : '' }))}
                >
                  <option value="">Seleccionar marco</option>
                  {marcos.map(m => (
                    <option key={m.id} value={m.id}>{m.marco}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Valor mínimo" htmlFor="valor_min">
                <Input
                  type="text"
                  id="valor_min"
                  value={regulado.valor_min}
                  onChange={(e) => setRegulado(prev => ({ ...prev, valor_min: e.target.value }))}
                  placeholder="0 si no aplica"
                />
              </FormField>
              <FormField label="Valor máximo" htmlFor="valor_max">
                <Input
                  type="text"
                  id="valor_max"
                  value={regulado.valor_max}
                  onChange={(e) => setRegulado(prev => ({ ...prev, valor_max: e.target.value }))}
                  placeholder="0 si no tiene máximo"
                />
              </FormField>
              <FormField label="Valor referencia" htmlFor="valor_ref">
                <Input
                  type="text"
                  id="valor_ref"
                  value={regulado.valor_ref}
                  onChange={(e) => setRegulado(prev => ({ ...prev, valor_ref: e.target.value }))}
                  placeholder="0 si no se toma en cuenta"
                />
              </FormField>
              <FormField label="Presencia" htmlFor="presencia" className="md:col-span-2">
                <Input
                  type="text"
                  id="presencia"
                  value={regulado.presencia}
                  onChange={(e) => setRegulado(prev => ({ ...prev, presencia: e.target.value }))}
                  placeholder="Texto aclaratorio si no tiene mínimos/máximos/referencia"
                />
              </FormField>
            </div>
          )}
        </div>

        {/* Sección ensayos controlados */}
        <div className="border-t border-slate-200 pt-4">
          <div className="mb-3">
            <Checkbox
              label="Ensayo controlado"
              id="esControlado"
              checked={esControlado}
              onChange={(e) => setEsControlado(e.target.checked)}
            />
          </div>
          {esControlado && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Organismo de control" htmlFor="id_organismo">
                <Select
                  id="id_organismo"
                  value={controlado.id_organismo}
                  onChange={(e) => setControlado(prev => ({ ...prev, id_organismo: e.target.value ? Number(e.target.value) : '' }))}
                >
                  <option value="">Seleccionar organismo</option>
                  {organismos.map(o => (
                    <option key={o.id} value={o.id}>{o.organismo}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Método de detección" htmlFor="metodo_deteccion">
                <Input
                  type="text"
                  id="metodo_deteccion"
                  value={controlado.metodo_deteccion}
                  onChange={(e) => setControlado(prev => ({ ...prev, metodo_deteccion: e.target.value }))}
                />
              </FormField>
              <FormField label="Límite de detección" htmlFor="limite_deteccion">
                <Input
                  type="text"
                  id="limite_deteccion"
                  value={controlado.limite_deteccion}
                  onChange={(e) => setControlado(prev => ({ ...prev, limite_deteccion: e.target.value }))}
                  placeholder="0 si no aplica"
                />
              </FormField>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default EnsayoForm;
